// ─────────────────────────────────────────────────────────────────────────────
// WP-16 REQ-140 — Modal legal bloqueante del primer acceso y de la re-aceptación.
//
// Privacidad + bases legales, VERSIONADOS: cuando el backend reporta algo pendiente
// (`LegalAcceptanceStatus.requiresAcceptance`) la app queda BLOQUEADA hasta aceptar. El contenido
// viene del backend (`useLegalDocument`), NO hardcodeado.
//
// El cuerpo del modal es el MISMO que el del registro (`LegalPolicyModal`): antes eran dos
// implementaciones de la misma cosa y ya habían divergido —el del registro tenía visor de PDF,
// portal, tamaño grande y aviso de error, y éste no tenía ninguna de las cuatro—. Aquí quedan sólo
// las dos diferencias reales de esta pantalla: **no se cierra** (es bloqueante) y **enseña la
// insignia de versión** (es una re-aceptación: la persona necesita ver qué versión se le pide).
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLegalStatus,
  useLegalDocument,
  useAcceptLegal,
  getRecognitionErrorMessage,
} from '@/api/recognition';
import type { LegalDocType } from '@/api/recognition';
import { LegalPolicyModal } from './LegalPolicyModal';

interface PolicyModalProps {
  type: LegalDocType;
  version: string;
  onAccepted: () => void;
  /** OU del usuario, tal como la resolvió el servidor en el endpoint de estado. */
  ouId?: string;
}

/** "1" -> "v1", "v1" -> "v1". Documentación ya publicó versiones con el prefijo escrito a mano. */
function versionLabel(version: string): string {
  return /^v/i.test(version) ? version : `v${version}`;
}

/**
 * Modal legal BLOQUEANTE. Añade al cuerpo común lo suyo: registrar la aceptación contra el backend.
 *
 * 📌 Se mandan las DOS versiones, y salen de sitios distintos a propósito:
 * - la **comparable** (`version`) viene del ESTADO (`pending[i].version`): es la del texto publicado,
 *   la que la compuerta compara;
 * - la **mostrada** (`displayedVersion`) la da el cuerpo común al aceptar: es el `pdf-{guid}` cuando
 *   lo que se pintó fue el PDF, y `null` cuando fue el texto.
 *
 * Si se grabara el `pdf-…` como versión, cada reemplazo de fichero volvería a lanzar este modal a
 * toda la plantilla; si no se grabara en ningún sitio, la firma diría que se aceptó un texto que la
 * persona no vio.
 */
export function PolicyModal({ type, version, onAccepted, ouId }: Readonly<PolicyModalProps>) {
  const { t } = useTranslation('recognition');
  const accept = useAcceptLegal();

  return (
    <LegalPolicyModal
      documentKey={type}
      ouId={ouId}
      versionLabel={versionLabel(version)}
      busy={accept.isPending}
      acceptError={accept.isError
        ? getRecognitionErrorMessage(accept.error, t('legal.acceptError'))
        : null}
      onAccept={(displayedVersion) => {
        accept.mutate({ type, version, displayedVersion }, { onSuccess: () => onAccepted() });
      }}
    />
  );
}

/**
 * Gate legal: consulta el estado de aceptaciones y, si hay versiones pendientes, BLOQUEA la app
 * mostrando los modales uno a uno hasta aceptarlos todos.
 *
 * 🔴 **Nadie se queda encerrado (D8).** El modal no tiene botón de cerrar —es lo que lo hace
 * bloqueante—, así que si el documento no se pudiera mostrar la app quedaría inaccesible **sin
 * salida**: el botón deshabilitado para siempre y ninguna forma de salir. Por eso, cuando el
 * documento pendiente no carga (ni PDF ni texto), este gate **no bloquea**. Es la misma postura
 * fail-open que ya tenía ante un fallo del endpoint de estado (`retry: 0` → error → no bloquea), y es
 * coherente: una caída de infraestructura no puede dejar a la plantilla sin app.
 *
 * Y no se «acepta por defecto» en ese caso a propósito: registrar el consentimiento de un documento
 * que la persona no pudo leer sería una firma falsa. Se deja pasar y se le volverá a pedir cuando el
 * documento vuelva a estar disponible.
 */
export function LegalGate({ children, enabled = true }: Readonly<{ children: ReactNode; enabled?: boolean }>) {
  // El gate legal se ata al mismo criterio de disponibilidad del backend de identidad. Mientras el
  // endpoint no exista, useLegalStatus (retry 0) da error → no bloquea. Cuando exista, bloquea real.
  const status = useLegalStatus(enabled);
  const { i18n } = useTranslation('recognition');
  const lang = i18n.language?.split('-')[0] ?? 'es';
  const [acceptedNow, setAcceptedNow] = useState<Set<string>>(new Set());

  const pending = (status.data?.pending ?? []).filter((p) => !acceptedNow.has(`${p.type}:${p.version}`));
  const next = pending[0];
  const ouId = status.data?.ouId ?? undefined;

  // Misma `queryKey` que la del modal, así que es UNA sola petición: aquí sólo se mira si hay algo
  // que enseñar antes de decidir si se bloquea.
  const doc = useLegalDocument(next?.type ?? null, lang, ouId);
  const documentoIndisponible = Boolean(next) && !doc.isLoading && (doc.isError || !doc.data);

  const blocking = Boolean(status.data?.requiresAcceptance)
    && pending.length > 0
    && !documentoIndisponible;

  if (blocking && next) {
    return (
      <PolicyModal
        key={`${next.type}:${next.version}`}
        type={next.type}
        version={next.version}
        ouId={ouId}
        onAccepted={() => setAcceptedNow((prev) => new Set(prev).add(`${next.type}:${next.version}`))}
      />
    );
  }

  return <>{children}</>;
}

export default LegalGate;
